import { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
  Scale,
} from 'chart.js';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, BarChart as ChartIcon, Globe2, Facebook } from 'lucide-react';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FaGoogle, FaTiktok } from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface CacheData {
  data: any[];
  timestamp: number;
  platform: string;
  startDate: string;
  endDate: string;
}

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos em milissegundos

const DailyMetricsChart = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['spend', 'clicks', 'conversions']);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: subDays(new Date(), 7),
    to: new Date()
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const platforms = useMemo(() => [
    { value: 'all', label: 'Todas Plataformas', icon: Globe2, color: 'zinc' },
    { value: 'facebook', label: 'Meta Ads', icon: Facebook, color: 'blue' },
    { value: 'google', label: 'Google Ads', IconComponent: FaGoogle, color: 'red' },
    { value: 'tiktok', label: 'TikTok Ads', IconComponent: FaTiktok, color: 'slate' }
  ], []);

  const dateRangePresets = useMemo(() => [
    { 
      label: 'Hoje',
      value: 'today',
      getRange: () => ({ from: new Date(), to: new Date() }),
      isActive: (from: Date, to: Date) => isToday(from) && isToday(to)
    },
    {
      label: 'Ontem',
      value: 'yesterday',
      getRange: () => {
        const yesterday = subDays(new Date(), 1);
        return { from: yesterday, to: yesterday };
      },
      isActive: (from: Date, to: Date) => isYesterday(from) && isYesterday(to)
    },
    {
      label: 'Últimos 7 dias',
      value: '7days',
      getRange: () => ({ from: subDays(new Date(), 7), to: new Date() }),
      isActive: (from: Date, to: Date) => 
        isSameDay(to, new Date()) && isSameDay(from, subDays(new Date(), 7))
    },
    {
      label: 'Últimos 30 dias',
      value: '30days',
      getRange: () => ({ from: subDays(new Date(), 30), to: new Date() }),
      isActive: (from: Date, to: Date) => 
        isSameDay(to, new Date()) && isSameDay(from, subDays(new Date(), 30))
    },
    {
      label: 'Esta semana',
      value: 'thisWeek',
      getRange: () => ({ 
        from: startOfWeek(new Date(), { locale: ptBR }), 
        to: endOfWeek(new Date(), { locale: ptBR }) 
      }),
      isActive: (from: Date, to: Date) => 
        isSameDay(from, startOfWeek(new Date(), { locale: ptBR })) &&
        isSameDay(to, endOfWeek(new Date(), { locale: ptBR }))
    },
    {
      label: 'Este mês',
      value: 'thisMonth',
      getRange: () => ({ 
        from: startOfMonth(new Date()), 
        to: endOfMonth(new Date()) 
      }),
      isActive: (from: Date, to: Date) => 
        isSameDay(from, startOfMonth(new Date())) &&
        isSameDay(to, endOfMonth(new Date()))
    }
  ], []);

  const metricsOptions = [
    { value: 'all', label: 'Todas Métricas' },
    { value: 'spend', label: 'Investimento' },
    { value: 'impressions', label: 'Impressões' },
    { value: 'clicks', label: 'Cliques' },
    { value: 'conversions', label: 'Conversões' },
    { value: 'ctr', label: 'Taxa de Clique (%)' },
    { value: 'cpc', label: 'Custo por Clique' }
  ];

  const colors = [
    { borderColor: '#FF3366', backgroundColor: '#FF6B6B' },
    { borderColor: '#4A4008', backgroundColor: '#666666' },
    { borderColor: '#000000', backgroundColor: '#333333' },
    { borderColor: '#999999', backgroundColor: '#CCCCCC' }
  ];

  const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
    if (range?.from && range?.to) {
      setDateRange({ from: range.from, to: range.to });
      setIsCalendarOpen(false);
    }
  };

  const calculateMetrics = (data: any[]) => {
    return data.map(day => ({
      ...day,
      ctr: day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0,
      cpc: day.clicks > 0 ? day.spend / day.clicks : 0
    }));
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    setGoogleError(false);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');

      // Verificar cache
      const cachedData = localStorage.getItem('metricsChartCache');
      if (cachedData) {
        const cache: CacheData = JSON.parse(cachedData);
        const now = Date.now();
        
        // Se o cache ainda é válido e corresponde aos parâmetros atuais
        if (now - cache.timestamp < CACHE_DURATION && 
            cache.platform === selectedPlatform &&
            cache.startDate === startDate &&
            cache.endDate === endDate) {
          setData(cache.data);
          setLoading(false);
          return;
        }
      }

      // Buscar dados do Google
      let googleData: any[] = [];
      if (selectedPlatform === 'all' || selectedPlatform === 'google') {
        try {
          const googleResponse = await fetch(
            `https://waffle-v2.thenewcc.workers.dev/metrics/google/${startDate}/${endDate}`,
            {
              headers: { 'Authorization': `Bearer ${user.token}` }
            }
          );
          if (!googleResponse.ok) throw new Error('Erro ao buscar dados do Google');
          const googleResult = await googleResponse.json();
          googleData = googleResult.data || [];
        } catch (error) {
          console.error('Erro ao carregar dados do Google:', error);
          setGoogleError(true);
        }
      }

      // Gerar array de datas
      const dates = [];
      let currentDate = new Date(dateRange.from);
      while (currentDate <= dateRange.to) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Buscar dados do Facebook e TikTok
      const platformsToFetch = ['facebook', 'tiktok'].filter(p => 
        selectedPlatform === 'all' || selectedPlatform === p
      );

      const platformsData = await Promise.all(
        dates.map(async (date) => {
          const formattedDate = format(date, 'yyyy-MM-dd');
          
          const platformResults = await Promise.all(
            platformsToFetch.map(async platform => {
              try {
                const response = await fetch(
                  `https://waffle-v2.thenewcc.workers.dev/metrics/${platform}/${formattedDate}/${formattedDate}`,
                  {
                    headers: { 'Authorization': `Bearer ${user.token}` }
                  }
                );
                if (!response.ok) throw new Error(`Erro ao buscar dados do ${platform}`);
                return await response.json();
              } catch (error) {
                console.error(`Erro ao carregar dados do ${platform}:`, error);
                return {
                  spend: 0,
                  impressions: 0,
                  clicks: 0,
                  conversions: 0
                };
              }
            })
          );

          return platformResults.reduce((acc, curr) => ({
            date: formattedDate,
            spend: (acc.spend || 0) + curr.spend,
            impressions: (acc.impressions || 0) + curr.impressions,
            clicks: (acc.clicks || 0) + curr.clicks,
            conversions: (acc.conversions || 0) + curr.conversions
          }), {
            date: formattedDate,
            spend: 0,
            impressions: 0,
            clicks: 0,
            conversions: 0
          });
        })
      );

      // Mesclar todos os dados
      const mergedData = dates.map(date => {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const googleMetrics = selectedPlatform === 'google' || selectedPlatform === 'all'
          ? (googleData.find(d => d.date === formattedDate) || {})
          : {};
        const otherMetrics = selectedPlatform === 'facebook' || selectedPlatform === 'tiktok' || selectedPlatform === 'all'
          ? (platformsData.find(d => d.date === formattedDate) || {})
          : {};

        return {
          date: formattedDate,
          spend: (googleMetrics.spend || 0) + (otherMetrics.spend || 0),
          impressions: (googleMetrics.impressions || 0) + (otherMetrics.impressions || 0),
          clicks: (googleMetrics.clicks || 0) + (otherMetrics.clicks || 0),
          conversions: (googleMetrics.conversions || 0) + (otherMetrics.conversions || 0)
        };
      });

      const calculatedData = calculateMetrics(mergedData);

      // Atualizar cache
      const cacheData: CacheData = {
        data: calculatedData,
        timestamp: Date.now(),
        platform: selectedPlatform,
        startDate,
        endDate
      };
      localStorage.setItem('metricsChartCache', JSON.stringify(cacheData));

      setData(calculatedData);
    } catch (error) {
      setError('Falha ao carregar os dados. Por favor, tente novamente.');
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      fetchAllData();
    }

    // Configurar intervalo de atualização
    const intervalId = setInterval(() => {
      if (dateRange.from && dateRange.to) {
        fetchAllData();
      }
    }, CACHE_DURATION);

    // Limpar intervalo quando o componente for desmontado
    return () => clearInterval(intervalId);
  }, [dateRange, selectedPlatform]);

  const chartData = useMemo(() => ({
    labels: data.map(item => 
      new Date(item.date).toLocaleDateString('pt-BR')
    ),
    datasets: selectedMetrics.map((metric, index) => ({
      label: metricsOptions.find(opt => opt.value === metric)?.label || metric,
      data: data.map(item => item[metric]),
      borderColor: colors[index % colors.length].borderColor,
      backgroundColor: colors[index % colors.length].backgroundColor,
      tension: 0.4,
      pointRadius: 6,
      pointHoverRadius: 8,
      pointBackgroundColor: colors[index % colors.length].borderColor,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      yAxisID: metric.includes('ctr') ? 'percentage' : (metric === 'spend' || metric === 'cpc' ? 'cost' : 'volume')
    }))
  }), [data, selectedMetrics]);

  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        backgroundColor: '#f5f5f5',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (tooltipItem: TooltipItem<'line'>) => {
            const value = tooltipItem.parsed.y;
            const metric = selectedMetrics[tooltipItem.datasetIndex];
            const label = metricsOptions.find(opt => opt.value === metric)?.label;
            
            if (metric === 'ctr') {
              return `${label}: ${value.toFixed(2)}%`;
            }
            if (metric === 'spend' || metric === 'cpc') {
              return `${label}: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
            }
            return `${label}: ${value.toLocaleString('pt-BR')}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      cost: {
        type: 'linear' as const,
        position: 'left' as const,
        beginAtZero: true,
        grid: {
          color: '#e5e5e5'
        },
        ticks: {
          font: {
            size: 12
          },
          callback: function(this: Scale<any>, value: number | string) {
            if (typeof value === 'number') {
              return `R$ ${value.toLocaleString('pt-BR')}`;
            }
            return value;
          }
        }
      },
      volume: {
        type: 'linear' as const,
        position: 'right' as const,
        beginAtZero: true,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          callback: function(this: Scale<any>, value: number | string) {
            if (typeof value === 'number') {
              return value.toLocaleString('pt-BR');
            }
            return value;
          }
        }
      },
      percentage: {
        type: 'linear' as const,
        position: 'right' as const,
        beginAtZero: true,
        max: 100,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          callback: function(this: Scale<any>, value: number | string) {
            if (typeof value === 'number') {
              return `${value}%`;
            }
            return value;
          }
        }
      }
    }
  }), [selectedMetrics]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg font-medium">Métricas</CardTitle>
        {googleError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>
              Não foi possível carregar os dados do Google. Os dados exibidos são apenas do Meta e TikTok.
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Seletor de Plataformas */}
            <div className="flex gap-2">
              {platforms.map(platform => {
                const Icon = platform.IconComponent || platform.icon;
                return (
                  <Button
                    key={platform.value}
                    variant={selectedPlatform === platform.value ? "default" : "outline"}
                    className={`h-9 px-4 flex items-center gap-2 transition-all ${
                      selectedPlatform === platform.value 
                        ? `bg-${platform.color}-600 hover:bg-${platform.color}-700 text-white` 
                        : `hover:bg-${platform.color}-50`
                    }`}
                    onClick={() => setSelectedPlatform(platform.value)}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{platform.label}</span>
                  </Button>
                );
              })}
            </div>

            {/* Seletor de Período */}
            <div className="flex flex-wrap gap-2">
              {dateRangePresets.map(preset => (
                <Button
                  key={preset.value}
                  variant={preset.isActive(dateRange.from, dateRange.to) ? "default" : "outline"}
                  className={`h-9 transition-all ${
                    preset.isActive(dateRange.from, dateRange.to)
                      ? 'bg-pink-600 hover:bg-pink-700 text-white'
                      : 'hover:bg-pink-50'
                  }`}
                  onClick={() => setDateRange(preset.getRange())}
                >
                  {preset.label}
                </Button>
              ))}
              
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`h-9 transition-all ${
                      !dateRangePresets.some(preset => preset.isActive(dateRange.from, dateRange.to))
                        ? 'border-pink-600 text-pink-600 hover:bg-pink-50'
                        : ''
                    }`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                    {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={handleDateRangeChange}
                    numberOfMonths={2}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-9 ml-auto transition-all hover:bg-pink-50 hover:text-pink-600"
              onClick={() => setChartType(prev => prev === 'line' ? 'bar' : 'line')}
            >
              <ChartIcon className="h-4 w-4 mr-2" />
              {chartType === 'line' ? 'Barras' : 'Linha'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg dark:bg-transparent">
            {metricsOptions.filter(m => m.value !== 'all').map((metric) => (
              <button
                key={metric.value}
                onClick={() => {
                  if (selectedMetrics.includes(metric.value)) {
                    setSelectedMetrics(prev => prev.filter(m => m !== metric.value));
                  } else if (selectedMetrics.length < 4) {
                    setSelectedMetrics(prev => [...prev, metric.value]);
                  }
                }}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedMetrics.includes(metric.value)
                    ? 'bg-black text-white dark:bg-white dark:text-black'
                    : 'bg-white border border-gray-300 hover:bg-gray-100 dark:bg-transparent dark:border-gray-600 dark:hover:bg-gray-800'
                }`}
                disabled={selectedMetrics.length >= 4 && !selectedMetrics.includes(metric.value)}
              >
                {metric.label}
              </button>
            ))}
          </div>
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center h-[400px]">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div style={{ height: '400px' }}>
              <Line data={chartData} options={options} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyMetricsChart;